import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';
import { useAxios } from '../../../hooks/use-axios';
import { baseUrls } from '../../../constants';
import { useAuthContext } from '../../../context/auth-context';
import AffiliationFilter from '../../../components/reports/AffiliationFilter';
import { getAffiliationZones, matchesAffiliationZone } from '../../../utils/report-filters';

const getCompletionColor = (pct) => {
  if (pct >= 100) return '#2e7d32';
  if (pct >= 50) return '#e65100';
  return '#c62828';
};

const getChipProps = (pct) => {
  if (pct >= 100) return { label: 'Completo',  sx: { backgroundColor: '#E6F4EA', color: '#1b5e20', fontWeight: 700 } };
  if (pct >= 50)  return { label: 'En progreso', sx: { backgroundColor: '#FFF3E0', color: '#e65100', fontWeight: 700 } };
  return { label: 'Pendiente', sx: { backgroundColor: '#FDECEA', color: '#b71c1c', fontWeight: 700 } };
};

const EventStatusCard = ({ summary, selected, onClick }) => {
  const color = getCompletionColor(summary.averageCompletion);
  const chip  = getChipProps(summary.averageCompletion);

  return (
    <Card
      onClick={onClick}
      sx={{
        mb: 1.5,
        cursor: 'pointer',
        backgroundColor: selected ? '#C1EFD0' : 'background.paper',
        transition: 'background-color 0.15s',
      }}
    >
      <CardActionArea sx={{ p: 0 }}>
        <Box sx={{ p: 1.5, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, flex: 1 }}>
              {summary.eventName}
            </Typography>
            <Chip size="small" {...chip} sx={{ ...chip.sx, fontSize: '0.68rem', height: 20 }} />
          </Box>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {summary.clubsPending} de {summary.totalClubs} clubes pendientes · {summary.averageCompletion}%
          </Typography>
        </Box>
        <Box sx={{
          height: 3,
          backgroundColor: color,
          width: `${Math.min(summary.averageCompletion, 100)}%`,
          transition: 'width 0.4s ease',
        }} />
      </CardActionArea>
    </Card>
  );
};

// Mobile: clubs list with accordion per club
const MobileClubsStep = ({ summary, eventDetail, loading, showOnlyPending, onTogglePending, onBack, zoneFilter = '', zones = [], onZoneChange }) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress sx={{ color: '#284B32' }} />
      </Box>
    );
  }

  if (!eventDetail) return null;

  const chip = getChipProps(summary.averageCompletion);
  const clubs = eventDetail.results
    .filter(c => !showOnlyPending || c.totalPending > 0)
    .filter(c => matchesAffiliationZone(c, zoneFilter))
    .slice()
    .sort((a, b) => a.completionPercentage - b.completionPercentage);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Back bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexShrink: 0 }}>
        <IconButton size="small" onClick={onBack} sx={{ color: '#284B32' }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#284B32', flex: 1, noWrap: true }}>
          {summary.eventName}
        </Typography>
        <Chip
          {...chip}
          label={`${summary.averageCompletion}%`}
          size="small"
          sx={{ ...chip.sx, fontSize: '0.68rem', height: 20 }}
        />
      </Box>

      {/* Summary metrics */}
      <Stack direction="row" spacing={2} sx={{ mb: 1.5, flexShrink: 0 }}>
        {[
          { label: 'Completos', value: eventDetail.summary.clubsCompleted, color: '#2e7d32' },
          { label: 'Pendientes', value: eventDetail.summary.clubsPending, color: '#c62828' },
          { label: 'Rúbricas', value: eventDetail.summary.totalRubrics, color: '#284B32' },
        ].map(m => (
          <Box key={m.label} sx={{ textAlign: 'center', flex: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">{m.label}</Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, color: m.color, lineHeight: 1 }}>{m.value}</Typography>
          </Box>
        ))}
      </Stack>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end', mb: 1.5, flexShrink: 0 }}>
        {zones.length > 0 && onZoneChange && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <AffiliationFilter
              value={zoneFilter}
              onChange={onZoneChange}
              zones={zones}
              size="small"
            />
          </Box>
        )}
        <Button
          size="small"
          variant={showOnlyPending ? 'contained' : 'outlined'}
          onClick={onTogglePending}
          sx={{ flexShrink: 0, ...(showOnlyPending ? { backgroundColor: '#284B32', '&:hover': { backgroundColor: '#1a3620' } } : {}) }}
        >
          {showOnlyPending ? 'Mostrar todos' : 'Solo pendientes'}
        </Button>
      </Box>

      <Divider sx={{ mb: 1.5, flexShrink: 0 }} />

      {/* Clubs accordion */}
      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        {clubs.map((club) => {
          const rubricsSorted = club.rubricDetails
            .slice()
            .sort((a, b) => {
              const pctA = a.maxPoints ? (a.grade / a.maxPoints) * 100 : 0;
              const pctB = b.maxPoints ? (b.grade / b.maxPoints) * 100 : 0;
              return pctA - pctB;
            });

          return (
            <Accordion
              key={club.clubId}
              disableGutters
              sx={{ mb: 1, borderRadius: 1, '&:before': { display: 'none' }, boxShadow: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5, py: 0.5, minHeight: 48 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                      {club.clubName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {club.affiliationName}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    <Chip
                      label={club.totalPending === 0 ? '✓' : `${club.totalPending} pend.`}
                      color={club.totalPending === 0 ? 'success' : 'error'}
                      size="small"
                      sx={{ fontSize: '0.65rem', height: 20 }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: getCompletionColor(club.completionPercentage), minWidth: 34, textAlign: 'right' }}
                    >
                      {club.completionPercentage}%
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0, pb: 1 }}>
                {/* Progress bar inside */}
                <LinearProgress
                  variant="determinate"
                  value={club.completionPercentage}
                  sx={{
                    height: 4,
                    mb: 1,
                    backgroundColor: 'rgba(40,75,50,0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: getCompletionColor(club.completionPercentage),
                    },
                  }}
                />
                {rubricsSorted.map((rubric, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      mx: 1.5,
                      mb: 0.75,
                      p: 1,
                      borderRadius: 1,
                      borderLeft: `3px solid ${rubric.isGraded ? '#2e7d32' : '#c62828'}`,
                      backgroundColor: rubric.isGraded ? '#f9fdf9' : '#fff8f8',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, flex: 1, lineHeight: 1.3 }}>
                        {rubric.rubricName}
                      </Typography>
                      <Chip
                        icon={rubric.isGraded ? <CheckCircleIcon sx={{ fontSize: 12 }} /> : <CancelIcon sx={{ fontSize: 12 }} />}
                        label={rubric.isGraded ? 'Calificado' : 'Pendiente'}
                        color={rubric.isGraded ? 'success' : 'error'}
                        size="small"
                        sx={{ fontSize: '0.6rem', height: 18, flexShrink: 0 }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {rubric.isGraded ? `${rubric.grade}/${rubric.maxPoints}` : `—/${rubric.maxPoints}`}
                      </Typography>
                      {rubric.savedBy && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {rubric.savedBy}
                        </Typography>
                      )}
                    </Box>
                    {rubric.comments && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontStyle: 'italic' }}>
                        {rubric.comments}
                      </Typography>
                    )}
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          );
        })}
        {clubs.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
            No hay clubes pendientes.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const ClubDrillDown = ({ summary, eventDetail, loading, showOnlyPending, onTogglePending, zoneFilter = '' }) => {
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    setExpandedRows({});
  }, [summary?.eventId]);

  const toggleRow = (clubId) => {
    setExpandedRows(prev => ({ ...prev, [clubId]: !prev[clubId] }));
  };

  if (!summary) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
        <Typography color="text.disabled">Selecciona un evento para ver el detalle.</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress sx={{ color: '#284B32' }} />
      </Box>
    );
  }

  if (!eventDetail) return null;

  const chip = getChipProps(summary.averageCompletion);
  const clubs = eventDetail.results
    .filter(c => !showOnlyPending || c.totalPending > 0)
    .filter(c => matchesAffiliationZone(c, zoneFilter))
    .slice()
    .sort((a, b) => a.completionPercentage - b.completionPercentage);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#284B32', flex: 1 }}>
          {summary.eventName}
        </Typography>
        <Chip
          {...chip}
          label={`${summary.averageCompletion}% completado`}
          sx={{ ...chip.sx, fontWeight: 700 }}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        {[
          { label: 'Clubes completos', value: eventDetail.summary.clubsCompleted, color: '#2e7d32' },
          { label: 'Clubes pendientes', value: eventDetail.summary.clubsPending, color: '#c62828' },
          { label: 'Total rúbricas', value: eventDetail.summary.totalRubrics, color: '#284B32' },
        ].map(m => (
          <Box key={m.label} sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" display="block">{m.label}</Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, color: m.color, lineHeight: 1 }}>{m.value}</Typography>
          </Box>
        ))}
        <Box sx={{ ml: 'auto' }}>
          <Button
            size="small"
            variant={showOnlyPending ? 'contained' : 'outlined'}
            onClick={onTogglePending}
            sx={showOnlyPending ? { backgroundColor: '#284B32', '&:hover': { backgroundColor: '#1a3620' } } : {}}
          >
            {showOnlyPending ? 'Mostrar todos' : 'Solo pendientes'}
          </Button>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell>Club</TableCell>
              <TableCell>Afiliación</TableCell>
              <TableCell align="center">Pendientes</TableCell>
              <TableCell sx={{ minWidth: 180 }}>Progreso</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clubs.map((club) => (
              <React.Fragment key={club.clubId}>
                <TableRow
                  hover
                  onClick={() => toggleRow(club.clubId)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <IconButton size="small">
                      {expandedRows[club.clubId] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell><strong>{club.clubName}</strong></TableCell>
                  <TableCell>{club.affiliationName}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={club.totalPending}
                      color={club.totalPending === 0 ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={club.completionPercentage}
                        sx={{
                          flex: 1,
                          height: 7,
                          borderRadius: 4,
                          backgroundColor: 'rgba(40,75,50,0.1)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: getCompletionColor(club.completionPercentage),
                            borderRadius: 4,
                          },
                        }}
                      />
                      <Typography variant="body2" sx={{ minWidth: 45, fontWeight: 700, color: getCompletionColor(club.completionPercentage) }}>
                        {club.completionPercentage}%
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                    <Collapse in={expandedRows[club.clubId]} timeout="auto" unmountOnExit>
                      <Box sx={{ mx: 2, my: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#284B32', mb: 1 }}>
                          Detalle de rúbricas
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Rúbrica</TableCell>
                              <TableCell align="right">Máx. puntos</TableCell>
                              <TableCell align="right">Calificación</TableCell>
                              <TableCell>Calificado por</TableCell>
                              <TableCell>Comentarios</TableCell>
                              <TableCell align="center">Estado</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {club.rubricDetails
                              .slice()
                              .sort((a, b) => {
                                const pctA = a.maxPoints ? (a.grade / a.maxPoints) * 100 : 0;
                                const pctB = b.maxPoints ? (b.grade / b.maxPoints) * 100 : 0;
                                return pctA - pctB;
                              })
                              .map((rubric, idx) => (
                              <TableRow key={idx} sx={{ backgroundColor: '#fafafa' }}>
                                <TableCell>{rubric.rubricName}</TableCell>
                                <TableCell align="right">{rubric.maxPoints}</TableCell>
                                <TableCell align="right">{rubric.isGraded ? rubric.grade : '—'}</TableCell>
                                <TableCell>{rubric.savedBy || '—'}</TableCell>
                                <TableCell>{rubric.comments || '—'}</TableCell>
                                <TableCell align="center">
                                  {rubric.isGraded ? (
                                    <Chip icon={<CheckCircleIcon />} label="Calificado" color="success" size="small" />
                                  ) : (
                                    <Chip icon={<CancelIcon />} label="Pendiente" color="error" size="small" />
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const PendingGradesReport = () => {
  const { activeCamp } = useAuthContext();
  const { post } = useAxios();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [summaries, setSummaries] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [eventDetail, setEventDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showOnlyPending, setShowOnlyPending] = useState(false);
  const [zoneFilter, setZoneFilter] = useState('');
  const [mobileStep, setMobileStep] = useState('events'); // 'events' | 'clubs'

  useEffect(() => {
    setSummaries([]);
    setSelectedIndex(null);
    setEventDetail(null);
    setMobileStep('events');
    setZoneFilter('');

    if (activeCamp?._id) {
      fetchSummary(activeCamp._id);
    }
  }, [activeCamp?._id]);

  // Push a history entry when entering the clubs step on mobile
  useEffect(() => {
    if (!isMobile) return;
    if (mobileStep === 'clubs') {
      window.history.pushState({ mobileStep: 'clubs' }, '');
    }
  }, [mobileStep, isMobile]);

  // Listen for browser back button / swipe gesture
  useEffect(() => {
    if (!isMobile) return;
    const handlePop = () => {
      if (mobileStep === 'clubs') {
        setMobileStep('events');
      }
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [mobileStep, isMobile]);

  const fetchSummary = async (campId) => {
    setLoadingSummary(true);
    try {
      const response = await axios.get(`${baseUrls.baseUrl}/rubric-grade/pending-summary/${campId}`);
      setSummaries(response.data.summaries || []);
    } catch (error) {
      console.error('Error fetching summary:', error);
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleSelectEvent = async (index) => {
    const summary = summaries[index];
    setSelectedIndex(index);
    setEventDetail(null);
    setShowOnlyPending(false);
    setLoadingDetail(true);
    if (isMobile) setMobileStep('clubs');
    try {
      const response = await post(`${baseUrls.baseUrl}/rubric-grade/pending-by-event`, {
        campId: activeCamp._id,
        eventId: summary.eventId,
      });
      setEventDetail(response.data);
    } catch (error) {
      console.error('Error fetching event detail:', error);
    } finally {
      setLoadingDetail(false);
    }
  };

  const zones = getAffiliationZones(eventDetail?.results || []);

  const handleMobileBack = () => {
    setMobileStep('events');
    // popstate will fire if user pressed the browser button; for the in-page
    // back button we just set state and let history.back() sync naturally.
    window.history.back();
  };

  const total = summaries.length;
  const completed = summaries.filter(s => s.averageCompletion >= 100).length;
  const withPending = summaries.filter(s => s.clubsPending > 0).length;
  const globalAvg = total > 0
    ? (summaries.reduce((a, s) => a + s.averageCompletion, 0) / total).toFixed(1)
    : '—';

  const metricCards = [
    { label: 'Total eventos',      value: total,       color: '#284B32' },
    { label: 'Promedio global',    value: `${globalAvg}%`, color: '#1b5e20' },
    { label: 'Eventos completos',  value: completed,   color: '#2e7d32' },
    { label: 'Con pendientes',     value: withPending, color: '#c62828' },
  ];

  // ── Mobile layout ──────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <Container maxWidth="sm" sx={{ pt: 3, pb: 3, height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {mobileStep === 'events' && (
          <>
            <Box sx={{ pl: 1.5, borderLeft: '4px solid #284B32', mb: 2, flexShrink: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#284B32', lineHeight: 1.2 }}>
                Calificaciones Pendientes
              </Typography>
            </Box>

            {!activeCamp?._id && (
              <Typography color="text.secondary">Selecciona un campamento para ver el reporte.</Typography>
            )}

            {activeCamp?._id && (
              <>
                <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mb: 2, flexShrink: 0 }}>
                  {metricCards.map(m => (
                    <Card key={m.label} sx={{ flex: '1 1 110px', minWidth: 0 }}>
                      <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.65rem' }}>{m.label}</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: m.color, lineHeight: 1.2 }}>{m.value}</Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>

                {loadingSummary && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                    <CircularProgress sx={{ color: '#284B32' }} />
                  </Box>
                )}

                {!loadingSummary && summaries.length === 0 && (
                  <Card sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">No hay eventos en este campamento.</Typography>
                  </Card>
                )}

                {summaries.length > 0 && (
                  <>
                    <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, fontWeight: 600, flexShrink: 0 }}>
                      {total} eventos — toca para ver detalle
                    </Typography>
                    <Box sx={{ overflowY: 'auto', flex: 1 }}>
                      {summaries.map((s, i) => (
                        <EventStatusCard
                          key={s.eventId}
                          summary={s}
                          selected={selectedIndex === i}
                          onClick={() => handleSelectEvent(i)}
                        />
                      ))}
                    </Box>
                  </>
                )}
              </>
            )}
          </>
        )}

        {mobileStep === 'clubs' && (
          <MobileClubsStep
            summary={selectedIndex !== null ? summaries[selectedIndex] : null}
            eventDetail={eventDetail}
            loading={loadingDetail}
            showOnlyPending={showOnlyPending}
            onTogglePending={() => setShowOnlyPending(p => !p)}
            onBack={handleMobileBack}
            zoneFilter={zoneFilter}
            zones={zones}
            onZoneChange={setZoneFilter}
          />
        )}
      </Container>
    );
  }

  // ── Desktop layout (unchanged) ─────────────────────────────────────────────
  return (
    <Container maxWidth="xl" sx={{
      pt: 4,
      pb: 2,
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      <Box sx={{ pl: 2, borderLeft: '4px solid #284B32', mb: 2, flexShrink: 0 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#284B32', lineHeight: 1.2 }}>
          Calificaciones Pendientes por Evento
        </Typography>
      </Box>

      {!activeCamp?._id && (
        <Typography color="text.secondary">Selecciona un campamento para ver el reporte.</Typography>
      )}

      {activeCamp?._id && (
        <>
          <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mb: 2, flexShrink: 0 }}>
            {metricCards.map(m => (
              <Card key={m.label} sx={{ flex: '1 1 140px', minWidth: 0 }}>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{m.label}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: m.color, lineHeight: 1.2 }}>{m.value}</Typography>
                </CardContent>
              </Card>
            ))}
          </Stack>

          {zones.length > 0 && (
            <Box sx={{ mb: 2, flexShrink: 0, maxWidth: 280 }}>
              <AffiliationFilter
                value={zoneFilter}
                onChange={setZoneFilter}
                zones={zones}
                size="small"
              />
            </Box>
          )}

          {loadingSummary && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <CircularProgress sx={{ color: '#284B32' }} />
            </Box>
          )}

          {!loadingSummary && summaries.length === 0 && (
            <Card sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No hay eventos en este campamento.</Typography>
            </Card>
          )}

          {summaries.length > 0 && (
            <Grid container spacing={3} sx={{ flex: 1, minHeight: 0 }}>
              <Grid item xs={12} md={4} sx={{ height: '100%' }}>
                <Box sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1.5, fontWeight: 600, flexShrink: 0 }}>
                    {total} eventos
                  </Typography>
                  <Box sx={{ overflowY: 'auto', flex: 1 }}>
                    {summaries.map((s, i) => (
                      <EventStatusCard
                        key={s.eventId}
                        summary={s}
                        selected={selectedIndex === i}
                        onClick={() => handleSelectEvent(i)}
                      />
                    ))}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={8} sx={{ height: '100%' }}>
                <Card sx={{
                  p: 2.5,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}>
                  <ClubDrillDown
                    summary={selectedIndex !== null ? summaries[selectedIndex] : null}
                    eventDetail={eventDetail}
                    loading={loadingDetail}
                    showOnlyPending={showOnlyPending}
                    onTogglePending={() => setShowOnlyPending(p => !p)}
                    zoneFilter={zoneFilter}
                  />
                </Card>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Container>
  );
};

export default PendingGradesReport;
